import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, CATALOGUES } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const CATEGORIES = [
  { key: "tous", emoji: "🛒", label: "Tous" },
  ...CATALOGUES,
  { key: "nouveautes", emoji: "🔥", label: "Nouveautés" },
];

const SORTS = [
  { key: "recent", label: "Nouveautés" },
  { key: "price_asc", label: "Prix croissant" },
  { key: "price_desc", label: "Prix décroissant" },
];

const CatalogueSection = ({ catalogue, index, products }) => (
  <section data-testid={`catalogue-section-${catalogue.key}`} className="mb-16 last:mb-0">
    <Reveal>
      <div className="flex items-end justify-between gap-4 border-b border-white/10 pb-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-blood tracking-[0.3em]">0{index + 1}</span>
          <h2 data-testid={`catalogue-title-${catalogue.key}`} className="font-syne font-extrabold uppercase tracking-tight text-2xl sm:text-3xl leading-none">
            <span className="mr-2" aria-hidden="true">{catalogue.emoji}</span>{catalogue.label}
          </h2>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ash whitespace-nowrap">
          {products.length} article{products.length > 1 ? "s" : ""}
        </span>
      </div>
    </Reveal>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
      {products.map((p, i) => (
        <ProductCard key={p.id} product={p} index={i} />
      ))}
    </div>
  </section>
);

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const validKeys = CATEGORIES.map((c) => c.key);
  const category = validKeys.includes(searchParams.get("cat")) ? searchParams.get("cat") : "tous";
  const setCategory = (key) => setSearchParams(key === "tous" ? {} : { cat: key });
  const [sort, setSort] = useState("recent");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "tous") params.category = category;
    api.get("/products", { params })
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = useMemo(() => {
    let list = products;
    if (search.trim()) {
      list = list.filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()));
    }
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price_desc") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, search, sort]);

  const grouped = useMemo(
    () => CATALOGUES.map((c) => ({ catalogue: c, items: filtered.filter((p) => p.category === c.key) })).filter((g) => g.items.length > 0),
    [filtered]
  );

  return (
    <main className="pt-16 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-blood mb-3">[ 4 Catalogues ]</p>
          <h1 className="font-syne font-extrabold uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-none mb-12">
            La Boutique<span className="text-blood">.</span>
          </h1>
        </Reveal>

        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10 border-y border-white/10 py-5">
          <div className="flex flex-wrap gap-2" role="tablist">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`filter-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`font-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${
                  category === c.key ? "border-blood bg-blood text-white" : "border-white/15 text-smoke hover:border-white/40 hover:text-white"
                }`}
              >
                <span className="mr-1.5" aria-hidden="true">{c.emoji}</span>{c.label}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ash" />
            <input
              data-testid="shop-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="RECHERCHER"
              className="bg-coal border border-white/15 pl-9 pr-4 py-2 font-mono text-xs uppercase tracking-[0.15em] placeholder:text-ash focus:outline-none focus:border-volt w-full lg:w-56"
            />
          </div>
          <select
            data-testid="shop-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-coal border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] focus:outline-none focus:border-volt"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>Trier : {s.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-ash py-20 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="shop-empty-message" className="font-mono text-xs uppercase tracking-[0.2em] text-ash py-20 text-center">
            Aucun produit trouvé
          </p>
        ) : category === "tous" || category === "nouveautes" ? (
          <div data-testid="product-catalog-grid">
            {grouped.map((g) => (
              <CatalogueSection key={g.catalogue.key} catalogue={g.catalogue} index={CATALOGUES.indexOf(g.catalogue)} products={g.items} />
            ))}
          </div>
        ) : (
          <div data-testid="product-catalog-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
